import React, { useState, useEffect } from 'react';
import {
  Clock,
  Calendar,
  CheckCircle2,
  AlertCircle,
  Kanban,
  Users,
  MessageSquare,
  ArrowRight,
  MapPin,
  Briefcase,
  Play,
  Square,
  Sparkles,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { dbHelpers } from '../../config/database';

export const EmployeeDashboard: React.FC = () => {
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [checkInTime, setCheckInTime] = useState<string | null>(null);
  const [workingStatus, setWorkingStatus] = useState<'present' | 'remote'>('present');
  const [attendanceRecords, setAttendanceRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [punchMsg, setPunchMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Live digital clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch employee's personal attendance history
  useEffect(() => {
    if (user?.id) {
      loadMyAttendance();
    }
  }, [user]);

  const loadMyAttendance = async () => {
    try {
      const res = await dbHelpers.getMyAttendance();
      if (res?.status && Array.isArray(res.data)) {
        setAttendanceRecords(res.data);
        // Check if checked in today
        const todayStr = new Date().toISOString().split('T')[0];
        const todayRecord = res.data.find(r => (r.date || '').startsWith(todayStr));
        if (todayRecord && todayRecord.check_in && !todayRecord.check_out) {
          setIsCheckedIn(true);
          setCheckInTime(new Date(todayRecord.check_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
        }
      }
    } catch (err) {
      console.error('Error loading attendance', err);
    }
  };

  const handlePunchToggle = async () => {
    setLoading(true);
    setPunchMsg(null);
    try {
      const loc = workingStatus === 'remote' ? 'Work From Home / Remote' : 'X2 InfoTech HQ, Gwalior';
      if (!isCheckedIn) {
        // Check In
        const res = await dbHelpers.checkIn({
          location: loc,
          workingStatus: workingStatus
        });
        if (res?.status) {
          setIsCheckedIn(true);
          const nowStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          setCheckInTime(nowStr);
          setPunchMsg({ type: 'success', text: `Successfully checked in at ${nowStr} (${workingStatus})!` });
          await loadMyAttendance();
        } else {
          setPunchMsg({ type: 'error', text: res?.message || 'Check-in failed.' });
        }
      } else {
        // Check Out
        const res = await dbHelpers.checkOut({
          location: loc
        });
        if (res?.status) {
          setIsCheckedIn(false);
          setPunchMsg({ type: 'success', text: `Successfully checked out. Good work today!` });
          await loadMyAttendance();
        } else {
          setPunchMsg({ type: 'error', text: res?.message || 'Check-out failed.' });
        }
      }
    } catch (err) {
      console.error(err);
      setPunchMsg({ type: 'error', text: 'Network error processing attendance punch.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 bg-slate-50 min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        {/* Header Bar */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5" />
                Employee Workspace
              </span>
              <span className="text-xs text-slate-500 font-medium">Department: {user?.department || 'Engineering'}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-2">
              Welcome back, {user?.name || 'Team Member'}!
            </h1>
            <p className="text-slate-600 text-sm mt-1">
              Here is your daily work portal. Log attendance, check sprint tasks, and collaborate with your team.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="#teams"
              className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-all flex items-center gap-2 border border-slate-300 shadow-sm"
            >
              <MessageSquare className="w-4 h-4 text-emerald-600" />
              Teams Chat
            </a>
            <a
              href="#board"
              className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl transition-all shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <Kanban className="w-4 h-4" />
              Sprint Board
            </a>
          </div>
        </div>

        {/* Attendance Punch & Digital Clock Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Digital Clock & Punch Widget */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col justify-between">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-100">
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Live Punch Station</span>
                <h3 className="text-lg font-bold text-slate-900">Today's Attendance</h3>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="statusMode"
                    checked={workingStatus === 'present'}
                    onChange={() => setWorkingStatus('present')}
                    className="accent-emerald-600"
                  />
                  In Office
                </label>
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="statusMode"
                    checked={workingStatus === 'remote'}
                    onChange={() => setWorkingStatus('remote')}
                    className="accent-emerald-600"
                  />
                  Work from Home
                </label>
              </div>
            </div>

            <div className="py-6 flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="space-y-1 text-center sm:text-left">
                <div className="text-3xl sm:text-4xl font-black text-slate-900 font-mono tracking-tight">
                  {currentTime.toLocaleTimeString()}
                </div>
                <div className="text-xs text-slate-500 font-medium">
                  {currentTime.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500 pt-2 justify-center sm:justify-start">
                  <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                  <span>{workingStatus === 'remote' ? 'Remote / Work From Home' : 'X2 InfoTech HQ, Gwalior'}</span>
                </div>
              </div>

              <div className="flex flex-col items-center gap-3">
                <button
                  onClick={handlePunchToggle}
                  disabled={loading}
                  className={`px-8 py-4 rounded-2xl font-bold text-base transition-all shadow-lg flex items-center gap-3 cursor-pointer ${
                    isCheckedIn
                      ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-amber-200'
                      : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-200'
                  }`}
                >
                  {isCheckedIn ? (
                    <>
                      <Square className="w-5 h-5 fill-current" />
                      Punch Out (End Shift)
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5 fill-current" />
                      Punch In (Start Shift)
                    </>
                  )}
                </button>
                {checkInTime && isCheckedIn && (
                  <span className="text-xs text-emerald-700 font-semibold bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">
                    Active since {checkInTime}
                  </span>
                )}
              </div>
            </div>

            {punchMsg && (
              <div
                className={`p-3 rounded-xl text-xs font-semibold flex items-center gap-2 ${
                  punchMsg.type === 'success'
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}
              >
                {punchMsg.type === 'success' ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                )}
                <span>{punchMsg.text}</span>
              </div>
            )}
          </div>

          {/* Quick Stats Column */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm flex items-center justify-between">
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase">Current Status</span>
                <div className="text-lg font-bold text-slate-900 mt-0.5">
                  {isCheckedIn ? (
                    <span className="text-emerald-600 flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                      Clocked In ({workingStatus})
                    </span>
                  ) : (
                    <span className="text-slate-500">Not Clocked In</span>
                  )}
                </div>
              </div>
              <Clock className="w-8 h-8 text-emerald-500" />
            </div>

            <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm flex items-center justify-between">
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase">Assigned Sprints</span>
                <div className="text-lg font-bold text-slate-900 mt-0.5">Sprint 14 Active</div>
              </div>
              <Kanban className="w-8 h-8 text-blue-500" />
            </div>

            <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm flex items-center justify-between">
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase">Paid Leave Balance</span>
                <div className="text-lg font-bold text-emerald-600 mt-0.5">14 Days Available</div>
              </div>
              <Calendar className="w-8 h-8 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Assigned Sprints & Tasks */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div>
              <h3 className="text-base font-bold text-slate-900">Assigned Sprint Tickets & Tasks</h3>
              <p className="text-xs text-slate-500">Active tasks from your current team sprint</p>
            </div>
            <a
              href="#board"
              className="text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1"
            >
              Open Full Jira Board <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2 hover:shadow-xs transition-shadow">
              <div className="flex justify-between items-center text-xs">
                <span className="font-mono text-[11px] font-bold text-blue-600">X2-104</span>
                <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold text-[10px]">
                  In Progress
                </span>
              </div>
              <h4 className="text-sm font-bold text-slate-900">Customer Letterhead PDF Generator Export</h4>
              <p className="text-xs text-slate-500 line-clamp-2">Ensure print styles and dynamic branding render with high DPI.</p>
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2 hover:shadow-xs transition-shadow">
              <div className="flex justify-between items-center text-xs">
                <span className="font-mono text-[11px] font-bold text-blue-600">X2-109</span>
                <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-semibold text-[10px]">
                  To Do
                </span>
              </div>
              <h4 className="text-sm font-bold text-slate-900">Client Project Milestones & Progress Tracking</h4>
              <p className="text-xs text-slate-500 line-clamp-2">Integrate milestone completion badges into the Client Portal.</p>
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2 hover:shadow-xs transition-shadow">
              <div className="flex justify-between items-center text-xs">
                <span className="font-mono text-[11px] font-bold text-blue-600">X2-112</span>
                <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-semibold text-[10px]">
                  Completed
                </span>
              </div>
              <h4 className="text-sm font-bold text-slate-900">Career Application Public Resume Uploading</h4>
              <p className="text-xs text-slate-500 line-clamp-2">Unauthenticated candidate resume handler with mime security check.</p>
            </div>
          </div>
        </div>

        {/* My Recent Attendance Records */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div>
              <h3 className="text-base font-bold text-slate-900">My Recent Attendance Logs</h3>
              <p className="text-xs text-slate-500">Your recorded check-ins and check-outs for this month</p>
            </div>
            <a
              href="#attendance"
              className="text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1"
            >
              View Full Logs <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50 text-slate-700 font-bold uppercase text-xs border-b border-slate-200">
                <tr>
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4">Check-In</th>
                  <th className="py-3 px-4">Check-Out</th>
                  <th className="py-3 px-4">Working Status</th>
                  <th className="py-3 px-4">Location</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {attendanceRecords.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-6 text-slate-400 text-xs">
                      No attendance logs found for your profile yet. Use the Punch In button above to record your attendance!
                    </td>
                  </tr>
                ) : (
                  attendanceRecords.slice(0, 5).map((rec, idx) => (
                    <tr key={rec.id || idx} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-4 font-semibold text-slate-900">{rec.date}</td>
                      <td className="py-3 px-4 font-mono text-xs text-emerald-700">
                        {rec.check_in ? new Date(rec.check_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td className="py-3 px-4 font-mono text-xs text-slate-500">
                        {rec.check_out ? new Date(rec.check_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 capitalize">
                          {rec.working_status || 'present'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-500">
                        {rec.check_in_location || 'Office HQ'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
export default EmployeeDashboard;
